import { formatLocaleCurrency, formatLocaleDate } from "@/utils/formatUtils";
import { tBoolean } from "@/utils/translationUtils";
import { isDefined } from "@/utils/utils";
import { Descriptions } from "antd";
import type { TFunction } from "i18next";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ClientsListView } from "../../../../../../client/components/ClientsListView";
import { ClientFormType } from "../../../../../../client/enums";
import { Client } from "../../../../../../client/types";
import { formatCoverageLimit } from "../../../../../../coveragelimit/utils";
import { InsuranceType } from "../../../../../enums";
import { StandardVehicleInsurance } from "../../../../../types";
import { VehicleData } from "./VehicleData";

export interface InsuranceDataViewItem {
  label: ReactNode;
  children: ReactNode;
  span?: number;
}

export const getVehicleInsuranceDataItems = (insurance: StandardVehicleInsurance, t: TFunction) => {
  const commonItems = {
    purpose: {
      label: t("calc.vehicle.enums.vehiclePurpose._label"),
      children: t("calc.vehicle.enums.vehiclePurpose." + insurance.insuranceData.purpose),
    },
    leasing: {
      label: t("contract.attrs.insurances.insuranceData.leasing"),
      children: tBoolean(insurance.insuranceData.leasing),
    },
  } satisfies Record<string, InsuranceDataViewItem>;

  switch (insurance.type) {
    case InsuranceType.MTPL:
      return {
        coverageLimit: {
          label: t("contract.attrs.insurances.coverageLimit"),
          children: formatCoverageLimit(insurance.coverageLimit),
        },
        ...commonItems,
        extendedAssistance: {
          label: t("contract.attrs.insurances.insuranceData.coverages.extendedAssistance"),
          children: tBoolean(insurance.insuranceData.coverages.extendedAssistance),
        },
        glass: {
          label: t("contract.attrs.insurances.insuranceData.coverages.glass"),
          children: (
            <>
              {tBoolean(insurance.insuranceData.coverages.glass)}{" "}
              {insurance.insuranceData.coverages.glassAmount ? (
                <>({formatLocaleCurrency(insurance.insuranceData.coverages.glassAmount, 0)})</>
              ) : undefined}
            </>
          ),
        },
        animal: {
          label: t("contract.attrs.insurances.insuranceData.coverages.animal"),
          children: tBoolean(insurance.insuranceData.coverages.animal),
        },
        element: {
          label: t("contract.attrs.insurances.insuranceData.coverages.element"),
          children: tBoolean(insurance.insuranceData.coverages.element),
        },
        injury: {
          label: t("contract.attrs.insurances.insuranceData.coverages.injury"),
          children: tBoolean(insurance.insuranceData.coverages.injury),
        },
        theft: {
          label: t("contract.attrs.insurances.insuranceData.coverages.theft"),
          children: tBoolean(insurance.insuranceData.coverages.theft),
        },
        vandalism: {
          label: t("contract.attrs.insurances.insuranceData.coverages.vandalism"),
          children: tBoolean(insurance.insuranceData.coverages.vandalism),
        },
      } satisfies Record<string, InsuranceDataViewItem>;
    case InsuranceType.CRASH:
      return {
        price: {
          label: t("contract.attrs.insurances.insuranceData.price"),
          children: (
            <span className="nowrap">
              {isDefined(insurance.insuranceData.price)
                ? formatLocaleCurrency(insurance.insuranceData.price)
                : undefined}
            </span>
          ),
        },
        complicity: {
          label: t("contract.attrs.insurances.complicity"),
          children: <span className="nowrap">{insurance.complicity?.value}</span>,
        },
        ...commonItems,
        extendedAssistance: {
          label: t("contract.attrs.insurances.insuranceData.coverages.extendedAssistance"),
          children: tBoolean(insurance.insuranceData.coverages.extendedAssistance),
        },
        glass: {
          label: t("contract.attrs.insurances.insuranceData.coverages.glass"),
          children: tBoolean(insurance.insuranceData.coverages.glass),
        },
        replacementVehicle: {
          label: t("contract.attrs.insurances.insuranceData.coverages.replacementVehicle"),
          children: tBoolean(insurance.insuranceData.coverages.replacementVehicle),
        },
        abroadVehicleRepair: {
          label: t("contract.attrs.insurances.insuranceData.coverages.abroadVehicleRepair"),
          children: tBoolean(insurance.insuranceData.coverages.abroadVehicleRepair),
        },
        gap: {
          label: t("contract.attrs.insurances.insuranceData.coverages.gap"),
          children: (
            <>
              {tBoolean(insurance.insuranceData.coverages.gap)}{" "}
              {insurance.insuranceData.coverages.gapDuration ? (
                <>({t("calc.vehicle.enums.gapDuration." + insurance.insuranceData.coverages.gapDuration)})</>
              ) : undefined}
            </>
          ),
        },
        gapComplicityReinsurance: {
          label: t("contract.attrs.insurances.insuranceData.coverages.gapComplicityReinsurance"),
          children: tBoolean(insurance.insuranceData.coverages.gapComplicityReinsurance),
          span: insurance.insuranceData.mtpl ? 3 : undefined,
        },
        ...(insurance.insuranceData.mtpl
          ? {
              mtplData: {
                label: t("contract.attrs.insurances.insuranceData.mtpl._label"),
                children: (
                  <Descriptions
                    size="small"
                    bordered
                    items={[
                      {
                        label: t("contract.attrs.insurances.coverageLimit"),
                        children: formatCoverageLimit(insurance.coverageLimit),
                      },
                      {
                        label: t("contract.attrs.insurances.insuranceData.mtpl.effectiveBeginningDate"),
                        children: formatLocaleDate(insurance.insuranceData.mtpl.effectiveBeginningDate),
                      },
                    ]}
                  />
                ),
              },
            }
          : {}),
      } satisfies Record<string, InsuranceDataViewItem>;
    case InsuranceType.GAP:
      return {
        duration: {
          label: t("calc.vehicle.enums.gapDuration._label"),
          children: t("calc.vehicle.enums.gapDuration." + insurance.insuranceData.duration),
        },
        buyingPrice: {
          label: t("contract.attrs.insurances.insuranceData.buyingPrice"),
          children: isDefined(insurance.insuranceData.buyingPrice)
            ? formatLocaleCurrency(insurance.insuranceData.buyingPrice)
            : undefined,
        },
        ...commonItems,
        complicityReinsurance: {
          label: t("contract.attrs.insurances.insuranceData.complicityReinsurance"),
          children: tBoolean(insurance.insuranceData.complicityReinsurance),
        },
      } satisfies Record<string, InsuranceDataViewItem>;
    case InsuranceType.PAS:
      return commonItems;
  }
};

interface Props {
  insurance: StandardVehicleInsurance;
  vehicleHolder?: Client;
  vehicleOwner?: Client;
}

export const VehicleInsuranceView = ({ insurance, vehicleHolder, vehicleOwner }: Props) => {
  const { t } = useTranslation();

  return (
    <>
      <VehicleData
        vehicle={insurance.vehicle}
        licensePlate={insurance.licensePlate}
        registrationCertificateNumber={insurance.insuranceData.registrationCertificateNumber}
      />

      <Descriptions
        className="margin-top-medium"
        styles={{ header: { marginBottom: 8 } }}
        size="small"
        bordered
        title={t("contract.sections.vehicleInsuranceData")}
        column={3}
        items={Object.values(getVehicleInsuranceDataItems(insurance, t))}
      />

      {vehicleHolder && vehicleOwner && (
        <ClientsListView
          className="margin-top-medium"
          clientsData={[
            {
              client: vehicleHolder,
              clientFormTypes: [ClientFormType.HOLDER],
            },
            {
              client: vehicleOwner,
              clientFormTypes: [ClientFormType.OWNER],
            },
          ]}
        />
      )}
    </>
  );
};
